/**
 * /oauth/authorize: the consent screen for `svcloud login` (and, later, any
 * other first-party client using the same PKCE flow, see
 * apps/cloud/src/services/oauth.ts's `OAUTH_CLIENTS`).
 *
 * The CLI opens this URL with the OAuth request in the query string. This
 * screen: (1) validates that request against cloud-api with an unauthenticated
 * `GET /oauth/authorize`, which is the same call a signed-out visitor's
 * browser can make; (2) if nobody is signed in, stashes this exact URL under
 * POST_LOGIN_PATH_KEY and sends the browser to /sign-in, so it lands back
 * here once Loop SSO completes; (3) once signed in, renders what is being
 * granted in plain SV Cloud language and lets the account approve or leave;
 * (4) on approval, `POST`s the same request with the session and follows the
 * `redirect_uri` cloud-api returns (a loopback URL with a one-time code),
 * which hands control back to the CLI's own listener.
 *
 * There is no consent UI here for anything past "approve" or "leave" because
 * there is nothing to configure: scope is per-client, not per-grant (see the
 * services/oauth.ts header cloud-api ships with), so every `svcloud login`
 * requests the exact same fixed scope set. Ratified 2026-08-25
 * (`apps/cloud/DEPLOYMENT.md` §10): deliberate for this audience, not a
 * stopgap. SV Cloud's users aren't expected to reason about individual
 * scopes, so one plain-language "Allow" granting the client's whole default
 * set is the intended design, permanently.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button } from '../../components/primitives/Button';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';
import { Wordmark } from '../../components/brand/Wordmark';
import { apiFetch, ApiError } from '../../lib/http';
import { getAccount } from '../../lib/api';
import { getToken, useSessionStore } from '../../lib/auth/session';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { POST_LOGIN_PATH_KEY } from './sso-state';

import '../../styles/screens/auth.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** Plain-language line for each scope the ceiling in oauth.ts can ever grant. Never the raw scope string. */
const SCOPE_COPY: Record<string, string> = {
  'projects:read': 'See your apps, their deploys, and their status',
  'projects:write': 'Create and delete apps',
  'deploys:write': 'Trigger deploys and roll back to an earlier one',
  'settings:write': 'Change app settings, including secrets',
  'database:read': 'Browse your apps’ databases',
  'database:write': 'Add, edit, and delete rows in your apps’ databases',
};

interface AuthorizePayload {
  client_id: string;
  client_name: string;
  redirect_uri: string;
  scopes: string[];
  state: string | null;
}

interface ApprovalResponse {
  redirect_uri: string;
  code: string;
}

export default function OauthAuthorize() {
  const navigate = useNavigate();
  const status = useSessionStore((s) => s.status);
  const setAccount = useSessionStore((s) => s.setAccount);
  const setStatus = useSessionStore((s) => s.setStatus);
  const [payload, setPayload] = useState<AuthorizePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  const search = window.location.search;

  // The demo build has no cloud-api oauth backend to validate against:
  // this screen only exists for a real CLI login.
  useEffect(() => {
    if (USE_MOCK) navigate('/projects', { replace: true });
  }, [navigate]);

  // If a session token exists in localStorage but account is not hydrated yet,
  // hydrate it here (OauthAuthorize renders outside AppShell).
  useEffect(() => {
    if (USE_MOCK || status !== 'loading' || !getToken()) return;
    getAccount()
      .then(setAccount)
      .catch(() => setStatus('signed-out'));
  }, [status, setAccount, setStatus]);

  useEffect(() => {
    if (USE_MOCK) return;
    apiFetch<AuthorizePayload>(`/api/v1/oauth/authorize${search}`, { unauthenticated: true })
      .then((res) => {
        setPayload(res);
        captureEvent('oauth_authorize_viewed', {
          client_id: res.client_id,
          client_name: res.client_name,
          scopes: res.scopes,
        });
      })
      .catch((err) => {
        const errorMsg = err instanceof ApiError
          ? err.message
          : t('This sign-in link is not valid. Ask the CLI to start over.');
        captureEvent('oauth_authorize_invalid', { error: errorMsg });
        setError(errorMsg);
      });
  }, [search]);

  // Not signed in: stash this exact URL and go sign in, then come straight
  // back here once Loop SSO hands the browser back to /sso/callback.
  useEffect(() => {
    if (USE_MOCK || !payload) return;
    if (status === 'signed-out' || !getToken()) {
      sessionStorage.setItem(POST_LOGIN_PATH_KEY, `${window.location.pathname}${search}`);
      navigate('/sign-in', { replace: true });
    }
  }, [payload, search, navigate, status]);

  function approve() {
    if (!payload) return;
    setApproving(true);
    setError(null);
    captureEvent('oauth_authorize_approved', {
      client_id: payload.client_id,
      scopes: payload.scopes,
    });
    const params = new URLSearchParams(search);
    apiFetch<ApprovalResponse>('/api/v1/oauth/authorize', {
      method: 'POST',
      body: {
        client_id: payload.client_id,
        redirect_uri: payload.redirect_uri,
        code_challenge: params.get('code_challenge'),
        code_challenge_method: params.get('code_challenge_method') ?? 'S256',
        scope: payload.scopes.join(' '),
        state: payload.state ?? undefined,
      },
    })
      .then((res) => {
        captureEvent('oauth_authorize_completed', { client_id: payload.client_id });
        window.location.href = res.redirect_uri;
      })
      .catch((err) => {
        setApproving(false);
        const errorMsg =
          err instanceof ApiError
            ? err.message
            : t('Could not complete sign-in. Ask the CLI to start over.');
        captureEvent('oauth_authorize_failed', { error: errorMsg });
        setError(errorMsg);
      });
  }

  function cancel() {
    captureEvent('oauth_authorize_cancelled', {
      client_id: payload?.client_id,
      scopes: payload?.scopes,
    });
    navigate('/projects');
  }

  const waitingForAccount = !USE_MOCK && payload && status !== 'signed-in' && getToken() !== null;

  return (
    <div className="auth">
      <HeroBackdrop reach="tight" />
      <div className="auth__lockup">
        <Wordmark size={32} />
      </div>
      <div className="auth__card">
        {error ? (
          <>
            <Notice tone="attention">
              <p>{error}</p>
            </Notice>
            <p className="auth__foot">
              <Link className="link-inline" to="/projects">
                {t('Back to SV Cloud')}
              </Link>
            </p>
          </>
        ) : !payload || waitingForAccount ? (
          <>
            <h1 className="auth__title">{t('Signing in')}</h1>
            <Skeleton label={t('Checking this sign-in request')} />
          </>
        ) : (
          <>
            <h1 className="auth__title">{t('Let {client} access your account?', { client: payload.client_name })}</h1>
            <ul className="auth__scopes">
              {payload.scopes.map((scope) => (
                <li key={scope}>{t(SCOPE_COPY[scope] ?? scope)}</li>
              ))}
            </ul>
            <div className="auth__wide">
              <Button variant="solid" loading={approving} onClick={approve}>
                {t('Allow {client}', { client: payload.client_name })}
              </Button>
            </div>
            <p className="auth__foot">
              <button type="button" className="link-inline" onClick={cancel}>
                {t('Not now')}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
